import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { useNotificationContext } from '../../contexts/NotificationContext'
import { useTagContext } from '../../contexts/TagContext'
import { api } from '../../services/fetch'
import type { Tag } from '../../types/tag'
import ConfirmDeclineModal from '../ConfirmDeclineModal'
import DeclineCommentModal from '../DeclineCommentModal'
import Modal from '../Modal'

interface AcceptTagModalProps {
  tag: Tag
  onClose: () => void
  onSuccess: () => void
}

const AcceptTagModal: React.FC<AcceptTagModalProps> = ({
  tag,
  onClose,
  onSuccess,
}) => {
  const { updateTagStatus, refreshTags } = useTagContext()
  const { tags: tagNotifications } = useNotificationContext()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showAcceptConfirmation, setShowAcceptConfirmation] = useState(false)
  const [showDeclineConfirmation, setShowDeclineConfirmation] = useState(false)
  const [showDeclineModal, setShowDeclineModal] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleAccept = async () => {
    setIsSubmitting(true)
    setError(null)
    try {
      await updateTagStatus(String(tag.id), 'ACCEPTED')

      // Mark tag as read when accepted
      try {
        await tagNotifications.markAsRead(tag.id)
      } catch (err) {
        console.error('Failed to mark tag as read:', err)
      }

      toast.success('Dispatch accepted successfully')
      onSuccess()
      onClose()
    } catch (err) {
      console.error('Failed to accept tag:', err)
      setError('Failed to accept dispatch. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAcceptClick = () => {
    setShowAcceptConfirmation(true)
  }

  const handleAcceptConfirmationYes = () => {
    setShowAcceptConfirmation(false)
    handleAccept()
  }

  const handleAcceptConfirmationNo = () => {
    setShowAcceptConfirmation(false)
  }

  const handleDeclineClick = () => {
    setShowDeclineConfirmation(true)
  }

  const handleDeclineConfirmationYes = () => {
    setShowDeclineConfirmation(false)
    handleDeclineConfirm('')
  }

  const handleDeclineConfirmationNo = () => {
    setShowDeclineConfirmation(false)
  }

  const handleDeclineConfirmationWithComment = () => {
    setShowDeclineConfirmation(false)
    setShowDeclineModal(true)
  }

  const handleDeclineConfirm = async (comment: string) => {
    setIsSubmitting(true)
    try {
      // Use the dedicated decline endpoint
      await api.patch(`/tag/${tag.id}/decline/`, {
        decline_reason: comment || null,
      })

      // Refresh tags to get updated data
      await refreshTags(true)

      // Mark tag as read when declined
      try {
        await tagNotifications.markAsRead(tag.id)
      } catch (err) {
        console.error('Failed to mark tag as read:', err)
      }

      toast.success('Dispatch declined successfully')
      onSuccess()
      onClose()
    } catch (err) {
      console.error('Failed to decline tag:', err)
      toast.error('Failed to decline dispatch')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeclineCancel = () => {
    setShowDeclineModal(false)
  }

  return (
    <Modal onClose={onClose}>
      <div className={css(styles.modal)}>
        <div className={css(styles.header)}>
          <h2 className={css(styles.title)}>Dispatch - Pending</h2>
          <div className={css(styles.tagLabel)}>{tag.label}</div>
        </div>

        <div className={css(styles.content)}>
          <div className={css(styles.detailsGrid)}>
            <div className={css(styles.detailRow)}>
              <span className={css(styles.detailLabel)}>Material:</span>
              <span className={css(styles.detailValue)}>
                {tag.material || '-'}
              </span>
            </div>
            <div className={css(styles.detailRow)}>
              <span className={css(styles.detailLabel)}>Pick up:</span>
              <span className={css(styles.detailValue)}>
                {tag.origin_address || '-'}
              </span>
            </div>
            <div className={css(styles.detailRow)}>
              <span className={css(styles.detailLabel)}>Drop off:</span>
              <span className={css(styles.detailValue)}>
                {tag.destination_address || '-'}
              </span>
            </div>
            {tag.notes && (
              <div className={css(styles.detailRow)}>
                <span className={css(styles.detailLabel)}>Notes:</span>
                <span className={css(styles.detailValue)}>{tag.notes}</span>
              </div>
            )}
          </div>

          {error && <div className={css(styles.errorMessage)}>{error}</div>}
        </div>

        <div className={css(styles.footer)}>
          <div className={css(styles.footerLeft)}>
            <button onClick={onClose} className={css(styles.closeBtn)}>
              Close
            </button>
          </div>
          <div className={css(styles.footerRight)}>
            <button
              onClick={handleDeclineClick}
              className={css(styles.declineBtn)}
            >
              Decline
            </button>
            <button
              onClick={handleAcceptClick}
              className={css(styles.acceptBtn)}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin" />
                  &nbsp;Accepting...
                </>
              ) : (
                'I Agree'
              )}
            </button>
          </div>
        </div>

        {/* Accept Confirmation Dialog */}
        {showAcceptConfirmation && (
          <div className={css(styles.confirmationOverlay)}>
            <div className={css(styles.confirmationModal)}>
              <div className={css(styles.confirmationHeader)}>
                <h3 className={css(styles.confirmationTitle)}>
                  Confirm Agreement
                </h3>
              </div>
              <div className={css(styles.confirmationContent)}>
                <p className={css(styles.confirmationText)}>
                  By selecting and then clicking on the [I Agree] button I
                  indicate I actually have read the provisions of the Tag Terms
                  and I expressly agree to the provisions of the Tag Terms.
                </p>
              </div>
              <div className={css(styles.confirmationFooter)}>
                <button
                  onClick={handleAcceptConfirmationNo}
                  className={css(styles.confirmationCancelBtn)}
                >
                  No, I made an error
                </button>
                <button
                  onClick={handleAcceptConfirmationYes}
                  className={css(styles.confirmationAcceptBtn)}
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? 'Processing...'
                    : 'Yes, I agree to the Tag Terms'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Decline Confirmation Dialog */}
        {showDeclineConfirmation && (
          <ConfirmDeclineModal
            entityName="Tag Terms"
            isSubmitting={isSubmitting}
            onConfirm={handleDeclineConfirmationYes}
            onDeclineWithComment={handleDeclineConfirmationWithComment}
            onCancel={handleDeclineConfirmationNo}
          />
        )}

        {/* Decline Reason Modal */}
        {showDeclineModal && (
          <DeclineCommentModal
            title="Decline Dispatch"
            onConfirm={handleDeclineConfirm}
            onCancel={handleDeclineCancel}
            isSubmitting={isSubmitting}
          />
        )}
      </div>
    </Modal>
  )
}

const styles = StyleSheet.create({
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '600px',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90vh',
  },
  header: {
    padding: '24px',
    borderBottom: '1px solid #E5E7EB',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
  },
  tagLabel: {
    fontSize: '14px',
    color: '#6B7280',
    marginTop: '4px',
  },
  content: {
    padding: '24px',
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
  },
  detailsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  detailRow: {
    display: 'flex',
    gap: '8px',
  },
  detailLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#6B7280',
    minWidth: '80px',
  },
  detailValue: {
    fontSize: '14px',
    color: '#111827',
  },
  footer: {
    padding: '24px',
    borderTop: '1px solid #E5E7EB',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerLeft: {
    display: 'flex',
    gap: '8px',
  },
  footerRight: {
    display: 'flex',
    gap: '8px',
  },
  closeBtn: {
    backgroundColor: '#F3F4F6',
    color: '#374151',
    border: '1px solid #D1D5DB',
    borderRadius: '4px',
    padding: '8px 16px',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: '#E5E7EB',
    },
  },
  declineBtn: {
    backgroundColor: '#EF4444',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    marginRight: '8px',
    cursor: 'pointer',
  },
  acceptBtn: {
    backgroundColor: '#2563EB',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    cursor: 'pointer',
  },
  errorMessage: {
    fontSize: '12px',
    color: '#DC2626',
    backgroundColor: '#FEE2E2',
    padding: '8px 12px',
    borderRadius: '4px',
    marginTop: '16px',
  },
  confirmationOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1200,
  },
  confirmationModal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '550px',
    margin: '20px',
  },
  confirmationHeader: {
    padding: '24px 24px 0 24px',
  },
  confirmationTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
  },
  confirmationContent: {
    padding: '16px 24px',
  },
  confirmationText: {
    margin: 0,
    fontSize: '14px',
    color: '#374151',
    lineHeight: 1.6,
  },
  confirmationFooter: {
    padding: '16px 24px 24px 24px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    flexWrap: 'wrap',
  },
  confirmationCancelBtn: {
    backgroundColor: '#F3F4F6',
    color: '#374151',
    border: 'none',
    borderRadius: '4px',
    padding: '10px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#E5E7EB',
    },
  },
  confirmationAcceptBtn: {
    backgroundColor: '#10B981',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '10px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#059669',
    },
    ':disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
})

export default AcceptTagModal
